import { type RefObject } from 'react';

import CameraButtons from '@cartwhl/ui/player/CameraButtons';

import { usePlayerCameraControls } from '../hooks/usePlayerCameraControls';
import type { PlayerCameraControlsHandle } from '../types';

export interface PlayerCameraButtonsProps {
  cameraControlsRef?: RefObject<PlayerCameraControlsHandle | null>;
  variant?: 'simple' | 'full';
  tooltipSide?: 'left' | 'right';
  inModal?: boolean;
  showFollow?: boolean;
  showFrame?: boolean;
  showReset?: boolean;
  frameCharacter?: () => void;
  disableFrameCharacter?: boolean;
}

export default function PlayerCameraButtons({
  cameraControlsRef,
  variant = 'simple',
  tooltipSide = 'right',
  inModal = false,
  showFollow = true,
  showFrame = true,
  showReset = false,
  frameCharacter,
  disableFrameCharacter = false,
}: PlayerCameraButtonsProps) {
  const {
    hasCameraControls,
    inputMode,
    setInputMode,
    follow,
    setFollow,
    zoomIn,
    zoomOut,
    frame,
    resetCamera,
  } = usePlayerCameraControls({ cameraControlsRef });

  const resolvedFrameCharacter =
    frameCharacter ??
    (() => {
      frame();
    });

  return (
    <CameraButtons.Root
      tooltipSide={tooltipSide}
      variant={variant}
      inModal={inModal}
    >
      <CameraButtons.InputMode
        inputMode={inputMode}
        setCameraInputMode={setInputMode}
        disabled={!hasCameraControls}
      />
      <CameraButtons.Zoom
        zoomIn={zoomIn}
        zoomOut={zoomOut}
        disabled={!hasCameraControls}
      />
      {showFrame && (
        <CameraButtons.Center
          centerCamera={resolvedFrameCharacter}
          disabled={!hasCameraControls || disableFrameCharacter}
          tooltipText="Frame character"
          text={variant === 'full' ? 'Frame' : undefined}
        />
      )}
      {showReset && (
        <CameraButtons.Reset
          resetCamera={resetCamera}
          disableCameraReset={!hasCameraControls}
        />
      )}
      {showFollow && (
        <CameraButtons.CameraFollow
          cameraFollow={follow}
          setCameraFollow={setFollow}
        />
      )}
    </CameraButtons.Root>
  );
}
